import * as React from 'react'
import clsx from 'clsx'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { useControlled } from '../../hooks'
import style from './index.module.scss'
import { capitalize } from '../../utils'
import { useFormControl } from '../FormControl'

export interface SwitchBaseClasses {
  root: string
  checked: string
  disabled: string
  input: string
  edgeStart: string
  edgeEnd: string
}

export interface SwitchBaseProps
  extends Omit<ButtonBaseProps, 'type' | 'onChange' | 'children' | 'value'> {
  autoFocus?: boolean
  /**
   * If `true`, the component is checked.
   */
  checked?: boolean
  /**
   * The icon to display when the component is checked.
   */
  checkedIcon?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<SwitchBaseClasses>
  /**
   * The default checked state. Use when the component is not controlled.
   */
  defaultChecked?: boolean
  /**
   * If `true`, the component is disabled.
   */
  disabled?: boolean
  /**
   * If `true`, the ripple effect is disabled.
   */
  disableRipple?: boolean
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple?: boolean
  /**
   * If given, uses a negative margin to counteract the padding on one
   * side (this is often helpful for aligning the left or right
   * side of the icon with content above or below, without ruining the border
   * size and shape).
   * @default false
   */
  edge?: 'start' | 'end' | false
  /**
   * The icon to display when the component is unchecked.
   */
  icon?: React.ReactNode
  /**
   * The id of the `input` element.
   */
  id?: string
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  /**
   * Pass a ref to the `input` element.
   */
  inputRef?: React.Ref<any>
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  readOnly?: boolean
  /**
   * If `true`, the `input` element is required.
   */
  required?: boolean
  tabIndex?: number
  type?: React.InputHTMLAttributes<HTMLInputElement>['type']
  /**
   * The value of the component. The DOM API casts this to a string.
   */
  value?: unknown
}

export const SwitchBase = React.forwardRef<HTMLButtonElement, SwitchBaseProps>(
  function SwitchBase(props, ref) {
    const {
      autoFocus,
      checked: checkedProp,
      checkedIcon,
      classes,
      className,
      defaultChecked,
      disabled: disabledProp,
      disableFocusRipple = false,
      edge = false,
      icon,
      id,
      inputProps,
      inputRef,
      name,
      onBlur,
      onChange,
      onFocus,
      readOnly,
      required,
      tabIndex,
      type,
      value,
      ...other
    } = props
    const [checked, setCheckedState] = useControlled({
      controlled: checkedProp,
      default: Boolean(defaultChecked),
      name: 'SwitchBase',
      state: 'checked',
    })

    const factorFormControl = useFormControl()

    const handleFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
      if (onFocus) {
        onFocus(event)
      }
      if (factorFormControl && factorFormControl.onFocus) {
        factorFormControl.onFocus(event as any)
      }
    }

    const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
      if (onBlur) {
        onBlur(event)
      }
      if (factorFormControl && factorFormControl.onBlur) {
        factorFormControl.onBlur(event as any)
      }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // Workaround for https://github.com/facebook/react/issues/9023
      if (event.nativeEvent.defaultPrevented) {
        return
      }

      const newChecked = event.target.checked

      setCheckedState(newChecked)

      if (onChange) {
        onChange(event)
      }
    }

    let disabled = disabledProp
    if (factorFormControl) {
      if (typeof disabled === 'undefined') {
        disabled = factorFormControl.disabled
      }
    }
    const hasLabelFor = type === 'checkbox' || type === 'radio'

    return (
      <ButtonBase
        Component="span"
        className={clsx(
          style.root,
          classes?.root,
          edge &&
            // @ts-ignore
            clsx(style[`edge-${edge}`], classes?.[`edge${capitalize(edge)}`]),
          disabled && classes?.disabled,
          checked && classes?.checked,
          className
        )}
        centerRipple
        focusRipple={!disableFocusRipple}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // @ts-ignore
        tabIndex={null}
        role={undefined}
        ref={ref}
        {...other}
      >
        {/* @ts-ignore */}
        <input
          /* eslint-disable-next-line */
          autoFocus={autoFocus}
          checked={checkedProp}
          defaultChecked={defaultChecked}
          className={clsx(style.input, classes?.input)}
          disabled={disabled}
          id={hasLabelFor ? id : ''}
          name={name}
          onChange={handleInputChange}
          readOnly={readOnly}
          ref={inputRef}
          required={required}
          tabIndex={tabIndex}
          type={type}
          {...(type === 'checkbox' && value === undefined ? {} : { value })}
          {...inputProps}
        />
        {checked ? checkedIcon : icon}
      </ButtonBase>
    )
  }
)
